import { FC, useState } from 'react';

import { EyeIconSmall, ThreeDotsIcon } from '../../../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/popover/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import { EventTypeBackend, TransactionSignatureFields } from '../../../../../types/events.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { EventSignatureStatusCell } from '../../../components/event-signature-status-cell';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { ShareDeltaBadge } from '../../../components/share-delta-badge';
import { FilesModal } from '../../../modals/index';
import {
  isPoolAdjustmentEvent,
  isPoolEvent,
  PoolEvent,
  resolvePoolEventRow,
} from '../../../utils/pool-event-row.util';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { SignatureStatusTd, Td } from './table-td';

/**
 * Pool creations and formal size adjustments. The Pools history tab (transaction view) lists all
 * of them in one table, so it always asks for — and lays out as — the pool creation type.
 */
export const PoolEvents: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);

  const tableEventType = props.transactionView ? EventTypeBackend.NEW_POOL : props.type;

  const { items, totalPages, isLoading } = EventHooks.useTransactions<
    PoolEvent & TransactionSignatureFields
  >({
    eventId: props.eventId,
    eventType: tableEventType,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  if (!isPoolEvent(props)) return <></>;

  // An adjustment's own card lays out as designed: equal columns and a row menu. Adjustments are
  // permanent, so the menu only views. The Pools tab keeps the shared transaction layout.
  const hasRowActions = !props.transactionView && isPoolAdjustmentEvent(props);

  const Transactions = (
    <>
      {items.map((event) => {
        if (!isPoolEvent(event)) return null;

        const { pool, signatureSignedCount, signatureStatus, signatureTotalCount } = event;
        const row = resolvePoolEventRow(event);

        return (
          <tr key={event.id}>
            <Td className="pl-6" value={format(row.date, 'dd/MM/yyyy')} />
            <Td value={<AppFormattedMessage id={row.transactionType} />} />
            <Td value={<Tooltip content={pool.name}>{pool.name}</Tooltip>} />
            <Td
              value={
                // Spread like the design's cell, but only across its width (a 229px column less
                // padding), so a wide screen keeps the badge by its number. Tall enough for either
                // badge, so rows with and without one line up. A count and change too long to sit
                // side by side wrap the badge under the number: the cell would clip it.
                <span className="flex min-h-6 w-full max-w-[197px] flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  {row.sharesCount.toLocaleString('en-US')}
                  {row.delta !== null && <ShareDeltaBadge delta={row.delta} />}
                </span>
              }
            />
            <Td
              value={<Tooltip content={pool?.shareClass?.name}>{pool?.shareClass?.name}</Tooltip>}
            />
            {props.transactionView && (
              <SignatureStatusTd
                value={
                  <EventSignatureStatusCell
                    signatureSignedCount={signatureSignedCount}
                    signatureStatus={signatureStatus}
                    signatureTotalCount={signatureTotalCount}
                  />
                }
              />
            )}
            {!props.transactionView && event.type === EventTypeBackend.NEW_POOL && (
              <Td
                value={
                  <EventTransactionFilesCell
                    date={event.date}
                    docusignFileLinks={event.docusignFileLinks}
                    filesLinks={event.filesLinks}
                    linkedSignatureFileLink={event.linkedSignatureFileLink}
                    onDocumentClick={handleDocumentClick}
                    onOpenFilesModal={handleOpenFilesModalForRow}
                    parentEvent={{
                      ...props,
                      pool: event.pool ?? props.pool,
                    }}
                    stakeholderName={pool.name}
                  />
                }
              />
            )}
            {hasRowActions && (
              <td className="w-12 px-3 py-6">
                <Popover>
                  {/* A flex box, not inline: no baseline gap to make the row taller. */}
                  <PopoverTrigger className="flex h-fit w-fit rounded data-[state=open]:shadow-md">
                    <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                      <ThreeDotsIcon />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                    <PopoverClose asChild>
                      <div
                        className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 motion-reduce:transition-none"
                        onClick={() => props.onView(props.id, event.id)}
                      >
                        <EyeIconSmall />
                        <span className="text-sm font-normal text-gray-700">
                          <AppFormattedMessage id="VIEW_DETAILS" />
                        </span>
                      </div>
                    </PopoverClose>
                  </PopoverContent>
                </Popover>
              </td>
            )}
          </tr>
        );
      })}
    </>
  );

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={props.name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        className={hasRowActions ? 'table-fixed' : undefined}
        eventType={tableEventType}
        transactionView={props.transactionView}
        {...tableStateProps}
      >
        {Transactions}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
